import {type AppBskyFeedDefs, type BskyAgent} from '@atproto/api'

import {type ArticlePreview, type NewsCategory} from '#/features/news/types'
import {NEWS_AUTHOR_HANDLE} from '#/lib/news/config'
import {getArticlePreview, getPostText, isArticleRoot} from '#/lib/news/articleParser'

const DEFAULT_LIMIT = 50

function extractHashtags(text: string): string[] {
  const matches = text.match(/#[\w]+/g) || []
  return matches.map(tag => tag.toLowerCase())
}

export function getCategoryIdForText(
  text: string,
  categories: NewsCategory[],
  fallbackId: string,
): string {
  const hashtags = extractHashtags(text)
  for (const category of categories) {
    if (!category.slug) continue
    const slugTag = `#${category.slug.toLowerCase()}`
    if (hashtags.includes(slugTag)) {
      return category.id
    }
  }
  return fallbackId
}

export function groupArticlesByCategory(
  previews: ArticlePreview[],
  fallbackId: string,
): Record<string, ArticlePreview[]> {
  return previews.reduce<Record<string, ArticlePreview[]>>((acc, preview) => {
    const categoryId = preview.categoryId || fallbackId
    if (!acc[categoryId]) acc[categoryId] = []
    acc[categoryId].push(preview)
    return acc
  }, {})
}

export async function fetchTellusArticlePreviews(
  agent: BskyAgent,
  categories: NewsCategory[],
  fallbackId: string,
  limit = DEFAULT_LIMIT,
): Promise<ArticlePreview[]> {
  const res = await agent.app.bsky.feed.getAuthorFeed({
    actor: NEWS_AUTHOR_HANDLE,
    limit,
    filter: 'posts_no_replies',
  })
  const posts =
    res.data.feed
      ?.map(item => item.post)
      .filter((post): post is AppBskyFeedDefs.PostView => Boolean(post)) ?? []

  // Strictly limit to Tellus author + tag so the News feed stays curated.
  const previews = posts
    .filter(isArticleRoot)
    .map(post => {
      const categoryId = getCategoryIdForText(
        getPostText(post),
        categories,
        fallbackId,
      )
      return getArticlePreview(post, categoryId)
    })
    .filter(Boolean) as ArticlePreview[]

  return previews.sort((a, b) => {
    const aTime = a.publishedAt ? Date.parse(a.publishedAt) : 0
    const bTime = b.publishedAt ? Date.parse(b.publishedAt) : 0
    return bTime - aTime
  })
}
